import {Component, Input, OnInit} from '@angular/core';
import {IncidentTransaction, ReferenceBookResponse, ReferenceBookType} from '@npck/af-frontend-api';
import {formatFromServerToUi, LocalizationService, NumberTriplesFormatPipe, TiynTengePipe} from '@npck/libs/commons';
import {forkJoin} from 'rxjs';
import {mapToViewModel} from '../../../../../../../components/files/file-preview/file-preview.component';
import {DictionaryHolderService, findValueInDictionary, resolveRefName} from '../../../../../../../domain/dictionary-holder.service';
import {AttachmentViewModel} from '../../../../documents-tab/documents-tab.component';

interface CreditField {
    label: string;
    value: string;
}

@Component({
    selector: 'af-transaction-credit-info-tab',
    templateUrl: 'incident-transaction-credit-info-tab.component.html',
    styleUrls: ['../transaction-details-tab/incident-transaction-details-tab.component.scss'],
    standalone: false,
})
export class IncidentTransactionCreditInfoTabComponent implements OnInit {
    @Input() transaction: IncidentTransaction;

    fields: CreditField[] = [];
    documents: AttachmentViewModel[] = [];

    private tiynTengePipe = new TiynTengePipe();
    private numberTriplesPipe = new NumberTriplesFormatPipe();

    constructor(
        private dictionaryHolderService: DictionaryHolderService,
        private localizationService: LocalizationService,
    ) {
    }

    ngOnInit(): void {
        const loan = this.transaction.loanInfo;
        if (!loan) {
            return;
        }
        forkJoin([
            this.dictionaryHolderService.getReference('loan_type'),
            this.dictionaryHolderService.getReference('loan_status'),
            this.dictionaryHolderService.getReference('suspension_reason'),
            this.dictionaryHolderService.getReference('forgiveness_reason'),
            this.dictionaryHolderService.getReference('currency'),
            this.dictionaryHolderService.getReference(ReferenceBookType.ATTACHED_DOC),
        ]).subscribe(([loanTypes, loanStatuses, suspensionReasons, forgivenessReasons, currencies, docTypes]) => {
            this.fields = [
                {label: 'i18n_af_labelLoanType', value: this.name(loanTypes, loan.type)},
                {label: 'i18n_af_labelLoanPhase', value: this.name(loanStatuses, loan.status)},
                {label: 'i18n_af_labelLoanDocNumber', value: loan.documentNumber || '-'},
                {label: 'i18n_af_labelLoanApplicationDate', value: this.date(loan.applicationDate)},
                {label: 'i18n_af_labelLoanStartDate', value: this.date(loan.startDate)},
                {label: 'i18n_af_labelLoanEndDate', value: this.date(loan.endDate)},
                {label: 'i18n_af_labelLoanActualRepaymentDate', value: this.date(loan.actualRepaymentDate)},
                {label: 'i18n_af_labelLoanAmount', value: this.money(loan.amount, loan.amountCurrency, currencies)},
                {label: 'i18n_af_labelLoanBalance', value: this.money(loan.balance, loan.balanceCurrency, currencies)},
                {label: 'i18n_af_labelIsSuspended', value: this.bool(loan.isSuspended)},
            ];
            if (loan.isSuspended) {
                this.fields.push(
                    {label: 'i18n_af_labelSuspensionReason', value: this.name(suspensionReasons, loan.suspensionReason)},
                    {label: 'i18n_af_labelSuspensionDate', value: this.date(loan.suspensionDate)},
                );
            }
            this.fields.push({label: 'i18n_af_labelIsForgiven', value: this.bool(loan.isForgiven)});
            if (loan.isForgiven) {
                this.fields.push(
                    {label: 'i18n_af_labelForgivenessReason', value: this.name(forgivenessReasons, loan.forgivenessReason)},
                    {label: 'i18n_af_labelForgivenessDate', value: this.date(loan.forgivenessDate)},
                    {label: 'i18n_af_labelForgivenessAmount', value:
                            this.money(loan.forgivenessAmount, loan.forgivenessCurrency, currencies)},
                );
            }
            this.documents = (loan.attachments ?? [])
                .map(attachment => mapToViewModel(attachment, docTypes))
                .filter(document => document.files?.length > 0);
        });
    }

    private name(dictionary: ReferenceBookResponse[], code?: string): string {
        return code ? findValueInDictionary(dictionary, code, 'name_ru') : '-';
    }

    private date(value?: string): string {
        return value ? formatFromServerToUi(value) : '-';
    }

    private money(value: number | undefined, currency: string | undefined, currencies: ReferenceBookResponse[]): string {
        if (value == null) {
            return '-';
        }
        const amount = this.numberTriplesPipe.transform(this.tiynTengePipe.transform(value));
        const currencyName = currency ? resolveRefName(currencies, currency) : null;
        return currencyName && currencyName !== currency ? `${amount} ${currencyName}` : `${amount}`;
    }

    private bool(value?: boolean): string {
        return this.localizationService.getLocalizedKey(value ? 'i18n_af_labelYes' : 'i18n_af_labelNo');
    }
}
